import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Sentry } from "@/lib/sentry";

export const Route = createFileRoute("/debug/sentry-test")({
  head: () => ({
    meta: [
      { title: "Sentry Test — internal" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SentryTestPage,
});

function SentryTestPage() {
  const [lastEventId, setLastEventId] = useState<string | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);

  const release =
    (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      ?.VITE_APP_RELEASE ?? "(unset)";

  function throwRenderError() {
    // Uncaught render error — routed through Sentry's ErrorBoundary/global handler.
    throw new Error(`Sentry render test @ ${new Date().toISOString()}`);
  }

  function throwAsyncError() {
    // Unhandled rejection — verifies async stack capture.
    setTimeout(() => {
      throw new Error(`Sentry async test @ ${new Date().toISOString()}`);
    }, 0);
  }

  function captureManualError() {
    const err = new Error(`Sentry manual test @ ${new Date().toISOString()}`);
    const id = Sentry.captureException(err, {
      tags: { test: "sentry-source-map-verify" },
    });
    setLastEventId(id ?? null);
    setCaptured(err.message);
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-12 text-sm">
      <h1 className="text-2xl font-semibold mb-2">Sentry test</h1>
      <p className="text-muted-foreground mb-6">
        Internal page. Use to verify releases and source maps.
      </p>

      <div className="mb-6 rounded-md border border-white/10 p-3">
        <div>
          <span className="opacity-70">Release: </span>
          <code>{release}</code>
        </div>
        {lastEventId ? (
          <div className="mt-2">
            <span className="opacity-70">Last event id: </span>
            <code>{lastEventId}</code>
          </div>
        ) : null}
        {captured ? (
          <div className="mt-2">
            <span className="opacity-70">Captured: </span>
            <code>{captured}</code>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={captureManualError}
          className="rounded-md border border-white/20 px-4 py-2 text-left hover:bg-white/5"
        >
          Capture manual exception (safe — page keeps running)
        </button>
        <button
          type="button"
          onClick={throwAsyncError}
          className="rounded-md border border-white/20 px-4 py-2 text-left hover:bg-white/5"
        >
          Throw async error (unhandled rejection)
        </button>
        <button
          type="button"
          onClick={throwRenderError}
          className="rounded-md border border-red-500/40 px-4 py-2 text-left text-red-300 hover:bg-red-500/10"
        >
          Throw render error (crashes this route)
        </button>
      </div>
    </div>
  );
}
